import { Alert, Button, Group, IconButton, iconSizes, Stack, Text } from '@common/ui'
import { IconHelpCircle } from '@common/ui/icons'
import { com, kotlin } from '@eidu/web'
import { useState } from 'react'
import FormPage from './FormPage'
import { OtherPagesProvider } from './OtherPagesProvider'
import NextOption from './NextOption'
import Page = com.eidu.sharedlib.form.definition.Page
import KtList = kotlin.collections.KtList
import { keyOf } from '../../util/withKey'

export type FormContentInputProps = {
  pages: KtList<Page>
  onChange?: (pages: KtList<Page>) => void
  errorMessage?: string
}

const FormContentInput = ({ pages, onChange, errorMessage }: FormContentInputProps) => {
  const [expandedPageKey, setExpandedPageKey] = useState<string | null>(null)

  const handleInsertPageAtStart = () => {
    if (!onChange) return

    const currentPages = pages.asJsReadonlyArrayView()
    const newPage = new Page('Untitled page', KtList.fromJsArray([]), `page_${Date.now()}`, null)

    const updatedPages = [newPage, ...currentPages]
    onChange(KtList.fromJsArray(updatedPages))
    setExpandedPageKey(keyOf(newPage))
  }

  const handleInsertPageAfter = (stableKey: string) => {
    if (!onChange) return

    const currentPages = pages.asJsReadonlyArrayView()
    const newPage = new Page('Untitled page', KtList.fromJsArray([]), `page_${Date.now()}`, null)

    const index = currentPages.findIndex((page) => keyOf(page) === stableKey)

    const updatedPages = [...currentPages.slice(0, index + 1), newPage, ...currentPages.slice(index + 1)]
    onChange(KtList.fromJsArray(updatedPages))
    setExpandedPageKey(keyOf(newPage))
  }

  const handleDeletePage = (stableKey: string) => {
    if (!onChange) return

    const currentPages = pages.asJsReadonlyArrayView()

    const index = currentPages.findIndex((page) => keyOf(page) === stableKey)

    const updatedPages = [...currentPages.slice(0, index), ...currentPages.slice(index + 1)]
    onChange(KtList.fromJsArray(updatedPages))

    if (stableKey === expandedPageKey) setExpandedPageKey(null)
  }

  const handlePageChange = (currentKey: string, newPage: Page) => {
    if (!onChange) return

    const currentPages = pages.asJsReadonlyArrayView()
    const updatedPages = currentPages.map((page) => (keyOf(page) === keyOf(newPage) ? newPage : page))
    onChange(KtList.fromJsArray(updatedPages))
  }

  const handleExpand = (stableKey: string) => {
    setExpandedPageKey(expandedPageKey === stableKey ? null : stableKey)
  }

  const [showInstructions, setShowInstructions] = useState(false)

  return (
    <>
      <Group
        gap="md"
        align="flex-start"
        wrap="nowrap"
        style={{ width: '100%', maxWidth: '100%', flexShrink: 1, boxSizing: 'border-box' }}
      >
        <Stack gap={0} style={{ flex: 1, minWidth: 0, boxSizing: 'border-box', overflow: 'hidden' }}>
          {showInstructions && (
            <Alert severity="info" withCloseButton onClose={() => setShowInstructions(false)}>
              <p>
                <Text>
                  You can add, remove, or edit <b>pages</b> in the form.
                </Text>
                <Text>Each page can include multiple containers, descriptions, and fields.</Text>
              </p>

              <p>
                <Text>
                  <b>Keys</b> for fields must be unique.
                </Text>
                <Text>
                  Keys should only contain lowercase letters, numbers, and underscores (<code>_</code>).
                </Text>
                <Text>
                  Keep keys short, but descriptive enough to understand the field’s purpose without needing the rest of
                  the form.
                </Text>
              </p>

              <p>
                <Text>
                  Switch to <b>YAML</b> mode to view or edit the form’s content as raw text.
                </Text>
              </p>

              <p>
                <Text>
                  For more information, in particular the details on the YAML format or requirements for support staff
                  activity forms, please see the{' '}
                  <a href="https://docs.google.com/document/d/1joS4EF6RcyEcPYX7m73irtBQ6d0okUV7sJIEWxALl8Y/edit?tab=t.0#heading=h.mhy9pgbgj7c3">
                    documentation
                  </a>
                  .
                </Text>
              </p>
            </Alert>
          )}

          {onChange && (
            <Group gap="md" align="center" my={8}>
              <IconButton aria-label="form content help" onClick={() => setShowInstructions(!showInstructions)}>
                <IconHelpCircle size={iconSizes.sm} stroke={1.5} />
              </IconButton>
            </Group>
          )}

          <Stack gap="md">
            {onChange && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleInsertPageAtStart()}
                style={{ alignSelf: 'center' }}
              >
                Insert Page
              </Button>
            )}

            {pages.asJsReadonlyArrayView().map((page) => {
              const otherPages: readonly NextOption[] = pages
                .asJsReadonlyArrayView()
                .filter((localPage) => localPage.key !== page.key)
                .flatMap((localPage) => {
                  const localKey = localPage.key
                  return localKey ? [{ next: { key: localKey, type: 'page' }, label: localPage.title }] : []
                })
              return (
                <OtherPagesProvider key={keyOf(page)} value={otherPages}>
                  <FormPage
                    page={page}
                    onInsertAfter={() => handleInsertPageAfter(keyOf(page))}
                    onDelete={onChange && (() => handleDeletePage(keyOf(page)))}
                    onChange={onChange && handlePageChange}
                    isExpanded={keyOf(page) === expandedPageKey}
                    onExpand={() => handleExpand(keyOf(page))}
                  />
                </OtherPagesProvider>
              )
            })}
          </Stack>
        </Stack>
      </Group>
      {errorMessage && <Alert severity="negative">{errorMessage}</Alert>}
    </>
  )
}

export default FormContentInput
